/** @jsx isolateComponent */

import { isolateComponent } from '@zapier/common-utils';
import { FlexLayout } from '@zapier/common-components';
import { Margin, Text } from '@zapier/design-system';

import { Currency } from '@zapier/design-system-revenue/src/types';
import { formatPriceToBeInCurrency } from '@zapier/design-system-revenue/src/utils';

type FancyPriceProps = {
  /** Price in USD pennies (i.e. hundreths of a USD dollar) */
  children: number;
  /** Currency to show price in */
  currency?: Currency;
};

const defaultCurrency: Currency = {
  icon: 'UnitedStates',
  multiplier: 1,
  name: 'USD',
  symbol: '$',
};

/**
 * Returns the decimal separator for the user's locale. Examples:
 *   - Returns . for English
 *   - Returns , for Swedish
 */
const getDecimalSeparator = () => {
  const value = 1.1;
  return value.toLocaleString().substring(1, 2);
};

/**
 * Displays a price in a styled and user-friendly format.
 */
export const FancyPrice = ({
  children,
  currency = defaultCurrency,
}: FancyPriceProps) => {
  const price = formatPriceToBeInCurrency(children, currency, false, {
    maxDecimals: 2,
    minDecimals: 0,
    useRelativeRounding: false,
  });
  const separator = getDecimalSeparator();
  const tokens = price.split(separator);

  return (
    <FlexLayout alignItems="top" gutter="none">
      <Margin margin="0 5px 0 0">
        <Text color="grey400" type="header700">
          {currency.symbol}
        </Text>
      </Margin>

      <Text color="grey700" type="header800">
        {tokens[0]}
      </Text>

      {tokens.length > 1 && (
        <Margin margin="5px 0 0 2px">
          <Text color="grey700" type="subtitle700">
            {separator}
            {tokens[1]}
          </Text>
        </Margin>
      )}
    </FlexLayout>
  );
};
